import { useMemo, useState } from "react";
import { formatDmy } from "../format/date";
import {
  PX_PER_DAY,
  addDays,
  axisTicks,
  classifyItem,
  daysBetween,
  estimateTitlePx,
  stackLanePx,
  todayLocal,
  type DatedItem,
  type Placed,
  type Zoom,
} from "./ganttLayout";
import type { Item } from "../replicache/keys";

// Read-only renderers for a shared/embedded task snapshot.  Extracted from
// SharedView so both the public share page (/shared/:token) and the
// Excalidraw embed page (/embed/:token) render identical trees and Gantt
// charts without duplicating layout logic.  These components are pure
// presentation — fetching, headers and persistence stay in the callers.

export type ShareScope = "group" | "item";
export type ItemPriority = "low" | "medium" | "high" | null;

export type SnapshotItem = {
  id: string;
  parentId: string | null;
  type: "task" | "header";
  title: string;
  notes: string;
  status: string;
  priority: ItemPriority;
  isFocused: boolean;
  startDate: string | null;
  dueDate: string | null;
  reportingTo: string | null;
  sortKey: string;
  isComplete: boolean;
};

export type SnapshotResponse = {
  scope: ShareScope;
  permissions: "read" | "read_tick" | "read_comment" | "edit";
  includeSubtree: boolean;
  group: { id: string; name: string; color: string } | null;
  rootItem: SnapshotItem | null;
  items: SnapshotItem[];
};

export type ViewMode = "tree" | "gantt";

// ---------------------------------------------------------------------------
// View toggle (top right of header)
// ---------------------------------------------------------------------------

export function ViewToggle({
  view,
  onView,
}: {
  view: ViewMode;
  onView: (v: ViewMode) => void;
}) {
  return (
    <div className="inline-flex overflow-hidden rounded-md border border-slate-300 text-sm dark:border-slate-600">
      <button
        onClick={() => onView("tree")}
        className={`px-3 py-1 ${
          view === "tree"
            ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
            : "bg-white text-slate-700 hover:bg-slate-100 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
        }`}
      >
        Tree
      </button>
      <button
        onClick={() => onView("gantt")}
        className={`px-3 py-1 ${
          view === "gantt"
            ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
            : "bg-white text-slate-700 hover:bg-slate-100 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
        }`}
      >
        Gantt
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tree
// ---------------------------------------------------------------------------

type Node = SnapshotItem & { children: Node[]; depth: number };

export function SharedTree({ data }: { data: SnapshotResponse }) {
  const tree = useMemo<Node[]>(() => {
    const byParent = new Map<string | null, SnapshotItem[]>();
    for (const it of data.items) {
      const k = it.parentId;
      const list = byParent.get(k);
      if (list) list.push(it);
      else byParent.set(k, [it]);
    }
    for (const list of byParent.values()) {
      list.sort((a, b) =>
        a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0,
      );
    }
    const build = (parentId: string | null, depth: number): Node[] =>
      (byParent.get(parentId) ?? []).map((it) => ({
        ...it,
        depth,
        children: build(it.id, depth + 1),
      }));

    if (data.scope === "item" && data.rootItem) {
      return [
        {
          ...data.rootItem,
          depth: 0,
          children: build(data.rootItem.id, 1),
        },
      ];
    }
    return build(null, 0);
  }, [data]);

  return (
    <ul role="tree" className="space-y-0.5">
      {tree.map((node) => (
        <NodeRow key={node.id} node={node} />
      ))}
    </ul>
  );
}

function NodeRow({ node }: { node: Node }) {
  const overdue =
    !node.isComplete &&
    node.dueDate !== null &&
    node.dueDate.slice(0, 10) < new Date().toISOString().slice(0, 10);

  return (
    <li>
      <div
        className="flex items-start gap-2 rounded-md px-2 py-1 hover:bg-slate-50 dark:hover:bg-slate-800"
        style={{ paddingLeft: `${node.depth * 20 + 8}px` }}
      >
        {node.type === "task" && (
          <>
            <span className="relative">
              {node.isFocused && (
                <span
                  aria-label="Currently focused"
                  title="Currently focused"
                  className="pointer-events-none absolute right-full top-1/2 mr-0.5 -translate-y-1/2 select-none text-amber-400 leading-none"
                >
                  ★
                </span>
              )}
              <SharedPriorityMarker priority={node.priority} />
            </span>
            <input
              type="checkbox"
              checked={node.isComplete}
              readOnly
              className="mt-0.5 h-4 w-4 rounded border-slate-300 dark:border-slate-600"
            />
          </>
        )}
        <div className="flex-1 min-w-0">
          <span
            className={
              node.type === "header"
                ? "font-semibold tracking-tight text-slate-800 dark:text-slate-100"
                : node.isComplete
                  ? "text-slate-400 line-through dark:text-slate-500"
                  : "text-slate-800 dark:text-slate-100"
            }
          >
            {node.title || (
              <span className="italic text-slate-400 dark:text-slate-500">(untitled)</span>
            )}
          </span>
          {(node.startDate || node.dueDate) && (
            <span
              className={`ml-2 text-[11px] ${
                overdue ? "text-rose-600 font-medium dark:text-rose-400" : "text-slate-500 dark:text-slate-400"
              }`}
            >
              {node.startDate ? formatDmy(node.startDate) : "?"} →{" "}
              {node.dueDate ? formatDmy(node.dueDate) : "?"}
            </span>
          )}
          {node.reportingTo && (
            <span className="ml-2 text-[11px] text-slate-500 dark:text-slate-400">
              ↑ {node.reportingTo}
            </span>
          )}
          {node.notes && (
            <p className="mt-0.5 whitespace-pre-wrap text-xs text-slate-600 dark:text-slate-300">
              {node.notes}
            </p>
          )}
        </div>
      </div>
      {node.children.length > 0 && (
        <ul role="group" className="space-y-0.5">
          {node.children.map((child) => (
            <NodeRow key={child.id} node={child} />
          ))}
        </ul>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Gantt — read-only.  Reuses the layout helpers from the editor; no drag,
// no parking lot, no create-from-lane.  Single combined lane (the share
// snapshot doesn't carry per-item groupId).
// ---------------------------------------------------------------------------

const TIME_AXIS_HEIGHT = 44;
const ROW_HEIGHT = 24;
const LANE_PADDING_Y = 8;
const BAR_HEIGHT = ROW_HEIGHT - 4;

export function SharedGantt({ data }: { data: SnapshotResponse }) {
  const [zoom, setZoom] = useState<Zoom>("week");
  const pxPerDay = PX_PER_DAY[zoom];

  const dated = useMemo<DatedItem[]>(() => {
    const out: DatedItem[] = [];
    for (const it of data.items) {
      if (it.type !== "task") continue;
      const cls = classifyItem(it as unknown as Item);
      if (cls) out.push(cls);
    }
    return out;
  }, [data]);

  const today = useMemo(todayLocal, []);
  const range = useMemo(() => {
    let start = addDays(today, -30);
    let end = addDays(today, 60);
    for (const d of dated) {
      if (d.start.getTime() < start.getTime()) start = new Date(d.start);
      if (d.end.getTime() > end.getTime()) end = new Date(d.end);
    }
    return { start: addDays(start, -7), end: addDays(end, 14) };
  }, [today, dated]);

  const totalDays = daysBetween(range.start, range.end);
  const chartWidth = Math.max(800, totalDays * pxPerDay);
  const dateToX = (d: Date) => daysBetween(range.start, d) * pxPerDay;
  const todayX = dateToX(today);

  // Title-aware lane stacking so a trailing label drops to the next row
  // instead of colliding with the following bar.
  const priorityOf = (id: string): ItemPriority =>
    (data.items.find((i) => i.id === id)?.priority ?? null) as ItemPriority;
  const { placed, rowCount } = useMemo(
    () =>
      stackLanePx(
        dated,
        (d) => daysBetween(range.start, d.start) * pxPerDay,
        (d) => {
          const barLeft = daysBetween(range.start, d.start) * pxPerDay;
          const barWidth =
            d.kind === "bar"
              ? Math.max(pxPerDay, (daysBetween(d.start, d.end) + 1) * pxPerDay)
              : Math.max(14, pxPerDay);
          const barRight = barLeft + barWidth;
          const titlePx = estimateTitlePx(d.item.title || "(untitled)");
          const markerPx = priorityOf(d.item.id) ? 8 : 0;
          const interior = d.kind === "bar" ? Math.max(0, barWidth - 12) : 0;
          const fitsInside = titlePx + markerPx + 4 <= interior;
          return fitsInside
            ? barRight
            : barRight + 6 + markerPx + titlePx + 4;
        },
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dated, range.start, pxPerDay, data.items],
  );

  const ticks = useMemo(() => axisTicks(range.start, range.end, zoom), [range, zoom]);
  const chartHeight =
    TIME_AXIS_HEIGHT + LANE_PADDING_Y * 2 + Math.max(1, rowCount) * ROW_HEIGHT;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        {(["day", "week", "month", "quarter"] as Zoom[]).map((z) => (
          <button
            key={z}
            onClick={() => setZoom(z)}
            className={`rounded-md border px-2 py-0.5 text-xs capitalize ${
              z === zoom
                ? "border-slate-900 bg-slate-900 text-white dark:border-slate-100 dark:bg-slate-100 dark:text-slate-900"
                : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            }`}
          >
            {z}
          </button>
        ))}
      </div>

      <div className="overflow-auto rounded-md border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
        <div className="relative" style={{ width: chartWidth, height: chartHeight }}>
          {/* Time axis */}
          <div
            className="sticky top-0 z-20 border-b border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900"
            style={{ height: TIME_AXIS_HEIGHT }}
          >
            {ticks.map((tick, i) => {
              const x = dateToX(tick.date);
              return (
                <div key={i}>
                  <div
                    className={`absolute top-0 ${
                      tick.major ? "border-l border-slate-300 dark:border-slate-600" : "border-l border-slate-100 dark:border-slate-800"
                    }`}
                    style={{ left: x, height: TIME_AXIS_HEIGHT }}
                  />
                  <div
                    className={`absolute top-2 whitespace-nowrap text-[11px] ${
                      tick.major ? "font-medium text-slate-700 dark:text-slate-200" : "text-slate-500 dark:text-slate-400"
                    }`}
                    style={{ left: x + 4 }}
                  >
                    {tick.label}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Vertical gridlines under the chart body */}
          {ticks.map((tick, i) => (
            <div
              key={`gl-${i}`}
              className={
                tick.major
                  ? "absolute border-l border-slate-200 dark:border-slate-700"
                  : "absolute border-l border-slate-50 dark:border-slate-800"
              }
              style={{
                left: dateToX(tick.date),
                top: TIME_AXIS_HEIGHT,
                height: chartHeight - TIME_AXIS_HEIGHT,
              }}
            />
          ))}

          {/* Today line */}
          {todayX >= 0 && todayX <= chartWidth && (
            <div
              className="absolute z-10 border-l-2 border-rose-500 pointer-events-none"
              style={{ left: todayX, top: 0, height: chartHeight }}
            >
              <div className="absolute left-1 top-1 rounded bg-rose-500 px-1 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white">
                Today
              </div>
            </div>
          )}

          {/* Bars + markers */}
          {placed.map((p) => (
            <SharedGanttItem
              key={p.item.id}
              placement={p}
              dateToX={dateToX}
              pxPerDay={pxPerDay}
              today={today}
              rowTop={TIME_AXIS_HEIGHT + LANE_PADDING_Y + p.rowIndex * ROW_HEIGHT}
              priority={
                (data.items.find((i) => i.id === p.item.id)?.priority ??
                  null) as ItemPriority
              }
              isFocused={
                data.items.find((i) => i.id === p.item.id)?.isFocused ?? false
              }
            />
          ))}
        </div>
      </div>

      {placed.length === 0 && (
        <p className="text-sm italic text-slate-500 dark:text-slate-400">
          No dated tasks to plot.
        </p>
      )}
    </div>
  );
}

function SharedGanttItem({
  placement,
  dateToX,
  pxPerDay,
  today,
  rowTop,
  priority,
  isFocused,
}: {
  placement: Placed;
  dateToX: (d: Date) => number;
  pxPerDay: number;
  today: Date;
  rowTop: number;
  priority: ItemPriority;
  isFocused: boolean;
}) {
  const { item, kind, start, end } = placement;
  const left = dateToX(start);
  const widthPx =
    kind === "bar"
      ? Math.max(pxPerDay, (daysBetween(start, end) + 1) * pxPerDay)
      : Math.max(14, pxPerDay);

  const overdue =
    !item.isComplete && kind !== "started" && end.getTime() < today.getTime();

  let bg = "bg-blue-500 text-white";
  if (item.isComplete) bg = "bg-slate-300 text-slate-600 line-through dark:bg-slate-700 dark:text-slate-300";
  else if (overdue) bg = "bg-rose-500 text-white";
  else if (item.status === "in_progress") bg = "bg-emerald-500 text-white";
  else if (item.status === "blocked") bg = "bg-amber-500 text-white";
  else if (item.status === "cancelled") bg = "bg-slate-300 text-slate-600 dark:bg-slate-700 dark:text-slate-300";

  const titleText = item.title || "(untitled)";
  const titlePx = estimateTitlePx(titleText);
  const markerPx = priority ? 8 : 0;
  const interior = kind === "bar" ? Math.max(0, widthPx - 12) : 0;
  const fitsInside = titlePx + markerPx + 4 <= interior;
  const tooltip = `${titleText} — ${item.startDate ?? "?"} → ${item.dueDate ?? "?"}`;

  return (
    <div
      title={tooltip}
      style={{ left, width: widthPx, top: rowTop, height: BAR_HEIGHT }}
      className="absolute"
    >
      <div
        className={`absolute inset-0 flex items-center justify-center rounded text-[11px] shadow-sm ${bg}`}
      >
        {kind === "due" && <span aria-hidden>◆</span>}
        {kind === "started" && <span aria-hidden>▶</span>}
      </div>
      {fitsInside ? (
        <span
          className={`pointer-events-none absolute inset-y-0 left-0 flex items-center gap-1 whitespace-nowrap pl-1.5 text-[11px] ${bg.includes("text-white") ? "text-white" : "text-slate-700 dark:text-slate-200"}`}
        >
          <SharedPriorityMarker priority={priority} />
          {isFocused && <SharedFocusStar />}
          <span>{titleText}</span>
        </span>
      ) : (
        <span
          className="pointer-events-none absolute inset-y-0 left-full ml-1 flex items-center gap-1 whitespace-nowrap text-[11px] text-slate-700 dark:text-slate-200"
        >
          <SharedPriorityMarker priority={priority} />
          {isFocused && <SharedFocusStar />}
          <span>{titleText}</span>
        </span>
      )}
    </div>
  );
}

function SharedFocusStar() {
  return (
    <span
      aria-label="Currently focused"
      title="Currently focused"
      className="select-none text-amber-400"
      style={{ fontSize: 12, lineHeight: 1 }}
    >
      ★
    </span>
  );
}

// ---------------------------------------------------------------------------
// Priority marker components
// ---------------------------------------------------------------------------

function SharedPriorityMarker({ priority }: { priority: ItemPriority }) {
  const colour =
    priority === "high"
      ? "bg-rose-500"
      : priority === "medium"
        ? "bg-amber-500"
        : priority === "low"
          ? "bg-sky-500"
          : "bg-transparent";
  const pulse = priority === "high" ? "animate-priority-pulse" : "";
  return (
    <span
      aria-label={priority ? `${priority} priority` : "no priority"}
      className={`mt-0.5 inline-block h-3.5 w-1 shrink-0 rounded-sm ${colour} ${pulse}`}
    />
  );
}

// (SharedPriorityMarker is the single rendering used for both tree-row
// and Gantt-bar priority, kept consistent with the editor.)

// ---------------------------------------------------------------------------
// Legend (priority colours)
// ---------------------------------------------------------------------------

export function PriorityLegend() {
  return (
    <section
      aria-label="Legend"
      className="mt-6 flex flex-wrap items-center gap-4 rounded-md border border-slate-200 bg-white px-4 py-3 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
    >
      <span className="font-medium text-slate-500 dark:text-slate-400">Priority</span>
      <span className="flex items-center gap-1.5">
        <span className="inline-block h-3.5 w-1 rounded-sm bg-rose-500 animate-priority-pulse" />
        High
      </span>
      <span className="flex items-center gap-1.5">
        <span className="inline-block h-3.5 w-1 rounded-sm bg-amber-500" />
        Medium
      </span>
      <span className="flex items-center gap-1.5">
        <span className="inline-block h-3.5 w-1 rounded-sm bg-sky-500" />
        Low
      </span>
      <span className="flex items-center gap-1.5">
        <span className="text-amber-400">★</span>
        Currently working on
      </span>
      <span className="ml-auto text-[11px] text-slate-400 dark:text-slate-500">
        High priority pulses · today line is red
      </span>
    </section>
  );
}
